'use client'

import { useAgentTradePerformanceChart } from '@repo/services'
import { LineChart, getCssVar, type LineConfig } from '@repo/shared/components'
import { useTranslate } from '@repo/shared/hooks'
import { Card, CenterBox, Col, Row, Typography } from '@repo/ui/components'
import type { UTCTimestamp } from 'lightweight-charts'
import { useTheme } from 'next-themes'
import { useEffect, useMemo, useState } from 'react'
import { TimePeriod } from './dashboard.types'
import Image from 'next/image'
import { useIsDesktop } from '@repo/ui/hooks'

const toUnixTimestamp = (isoUtcString: string): UTCTimestamp =>
  Math.floor(new Date(isoUtcString).getTime() / 1000) as UTCTimestamp

export const DashboardChart = ({ timePeriod }: { timePeriod: TimePeriod }) => {
  const { t } = useTranslate()
  const { resolvedTheme } = useTheme()
  const isDesktop = useIsDesktop()

  const { data, isLoading } = useAgentTradePerformanceChart({
    period: timePeriod,
  })

  const [colors, setColors] = useState<string[]>([
    '#3B82F6',
    '#3B82F6',
    '#3B82F6',
  ])

  useEffect(() => {
    setTimeout(() => {
      setColors([
        getCssVar('--color-alert-main'),
        getCssVar('--color-base-main'),
        getCssVar('--color-positive-main'),
      ])
    }, 50)
  }, [resolvedTheme])

  type customData = Array<Pick<LineConfig, 'id' | 'data' | 'color' | 'label'>>

  // Line data for chart
  const lineData: customData = useMemo(() => {
    const raw = data?.data?.data || []

    if (raw.length === 0) return []

    // Create series for different metrics
    const incomeData = raw.map((item) => ({
      time: toUnixTimestamp(new Date(item.time ?? 0).toISOString()),
      value: 0,
    }))

    const tokensData = raw.map((item) => ({
      time: toUnixTimestamp(new Date(item.time ?? 0).toISOString()),
      value: 0,
    }))

    const tradesData = raw.map((item) => ({
      time: toUnixTimestamp(new Date(item.time ?? 0).toISOString()),
      value: item.otcTrades || 0,
    }))

    return [
      {
        id: 'incomeData',
        data: incomeData,
        color: colors[0] as string,
        label: t('dashboardAgent_chart_views'),
      },
      {
        id: 'tokensData',
        data: tokensData,
        color: colors[1] as string,
        label: t('dashboardAgent_chart_registers'),
      },
      {
        id: 'tradesData',
        data: tradesData,
        color: colors[2] as string,
        label: t('dashboardAgent_chart_trades'),
      },
    ]
  }, [data?.data?.data, t, colors])

  // State for line visibility
  const [lineVisibility, setLineVisibility] = useState<Record<string, boolean>>(
    {
      incomeData: true,
      tokensData: true,
      tradesData: true,
    },
  )

  // Lines configuration
  const linesConfig: LineConfig[] = useMemo(() => {
    if (
      lineData[0]?.data?.length === 0 ||
      lineData[1]?.data?.length === 0 ||
      lineData[2]?.data?.length === 0
    )
      return []

    const emptyData = {
      id: '',
      data: [],
      label: '',
      color: '',
    }

    const incomeData = lineData[0] ?? emptyData
    const tokensData = lineData[1] ?? emptyData
    const tradesData = lineData[2] ?? emptyData

    return [
      {
        id: incomeData.id,
        data: incomeData.data,
        label: incomeData.label,
        color: String(incomeData.color),
        lineType: 'solid' as const,
        hasGradient: false,
        lineWidth: 2,
        visible: lineVisibility[incomeData.id],
        onlyValue: true,
      },
      {
        id: tokensData.id,
        data: tokensData.data,
        label: tokensData.label,
        color: String(tokensData.color),
        lineType: 'solid' as const,
        hasGradient: false,
        lineWidth: 2,
        visible: lineVisibility[tokensData.id],
        onlyValue: true,
      },
      {
        id: tradesData.id,
        data: tradesData.data,
        label: tradesData.label,
        color: String(tradesData.color),
        lineType: 'solid' as const,
        hasGradient: false,
        lineWidth: 2,
        visible: lineVisibility[tradesData.id],
        onlyValue: true,
      },
    ]
  }, [lineData, lineVisibility])

  // Toggle line visibility
  const toggleLineVisibility = (lineId: string) => {
    setLineVisibility((prev) => ({
      ...prev,
      [lineId]: !prev[lineId],
    }))
  }

  return (
    <Card>
      <Row>
        <Col>
          <Typography size='md' weight='bold' className='whitespace-nowrap'>
            {t(`dashboardAgent_chart_title_${timePeriod}`)}
          </Typography>
        </Col>
      </Row>

      {lineData.length === 0 && !isLoading && (
        <CenterBox className=' flex-col gap-lg py-10xl'>
          <Image
            src='/images/chart-empty.svg'
            alt='coin'
            width={64}
            height={64}
          />
          <Typography
            variant='p'
            size='sm'
            color='text-base-surface-100'
            className='text-center'
          >
            {t('dashboardAgent_chart_title_empty')}
          </Typography>
        </CenterBox>
      )}
      {lineData.length > 0 && !isLoading && (
        <Row className='gap-lg items-stretch md:flex-row flex-col-reverse'>
          <Col className='justify-between sm:justify-center w-full md:min-w-[180px] max-w-full md:max-w-[220px] gap-2xl md:flex-col flex-row-reverse'>
            {linesConfig.map((line, index) => (
              <div
                key={line.id + index}
                onClick={() => toggleLineVisibility(line.id)}
                className='cursor-pointer'
              >
                <Col className='items-center gap-xs w-fit sm:w-full hover:opacity-80 transition-opacity'>
                  <span
                    className='cursor-pointer inline-block w-6 h-1 rounded bg-transparent'
                    style={{
                      backgroundColor: line.color,
                      opacity: line.visible ? 1 : 0.3,
                    }}
                  />
                  <Typography
                    size='sm'
                    color={
                      line.visible
                        ? 'text-neutral-main'
                        : 'text-neutral-surface-60'
                    }
                    className={line.visible ? '' : 'line-through'}
                  >
                    {line.label}
                  </Typography>
                </Col>
              </div>
            ))}
          </Col>
          <Col className='flex-1 rounded-md p-0 md:p-md ltr min-h-[400px]'>
            <LineChart
              className='w-full h-[400px] sm:h-full'
              lines={linesConfig}
              showCustomTooltip={true}
              formatPriceCompact={!isDesktop}
            />
          </Col>
        </Row>
      )}
    </Card>
  )
}
